import { z } from "zod";
import {
  storedWorkRequestAttachmentSchema,
  WORK_REQUEST_ATTACHMENT_MAX_FILES,
} from "@shared/work-request";

// --- Contact Step ---

export const contactStepSchema = z.object({
  fullName: z.string().min(2, "Full name is required"),
  email: z.string().email("Valid email is required"),
  company: z.string().min(2, "Company name is required"),
  websiteUrl: z.string().url("Valid URL required").optional().or(z.literal("")),
  phone: z.string().optional(),
});

export type ContactStepData = z.infer<typeof contactStepSchema>;

// --- Category-specific schemas ---

export const websiteChangeSchema = z.object({
  pageOrUrl: z.string().optional(),
  changeSummary: z.string().min(10, "Please describe the change in 1-2 sentences"),
  loginOrAccessNotes: z.string().optional(),
});

export const techSupportSchema = z.object({
  issueSummary: z.string().min(10, "Please describe the issue"),
  impact: z.enum(["site_down", "broken_feature", "cosmetic", "other"], {
    required_error: "Please select impact level",
  }),
});

export const creativeDesignSchema = z.object({
  assetType: z.string().min(3, "Please specify the asset type"),
  usageDeadline: z.string().optional(),
  contentOrCopyAvailable: z.enum(["yes", "no"], {
    required_error: "Please indicate if content is available",
  }),
});

export const otherRequestSchema = z.object({
  shortDescription: z.string().min(10, "Please provide a brief description"),
});

export const newProjectSchema = z.object({
  projectSummary: z.string().min(15, "Please describe the project"),
  goals: z.string().min(10, "Share your primary goals"),
  targetAudience: z.string().optional(),
  keyDeliverables: z.string().optional(),
  budgetRange: z.enum(["under_5k", "5k_15k", "15k_30k", "30k_plus", "unsure"], {
    required_error: "Please select a budget range",
  }),
  timeline: z.enum(["asap", "one_month", "two_three_months", "flexible", "unsure"], {
    required_error: "Please select a timeline",
  }),
  successMeasures: z.string().optional(),
  stakeholders: z.string().optional(),
});

// --- Request Details Step ---

export const requestDetailsStepSchema = z.object({
  category: z.enum(["website_change", "tech_support", "creative_design", "other", "new_project"], {
    required_error: "Please select a category",
  }),
  requestDetails: z.union([
    websiteChangeSchema,
    techSupportSchema,
    creativeDesignSchema,
    otherRequestSchema,
    newProjectSchema,
  ]),
  loomUrl: z.string().url("Valid Loom URL required").optional().or(z.literal("")),
  attachments: z
    .array(storedWorkRequestAttachmentSchema)
    .max(
      WORK_REQUEST_ATTACHMENT_MAX_FILES,
      `Maximum ${WORK_REQUEST_ATTACHMENT_MAX_FILES} files allowed`,
    )
    .optional(),
});

export type RequestDetailsStepData = z.infer<typeof requestDetailsStepSchema>;

// --- Commercial Step ---

export const commercialStepSchema = z.object({
  agreedToTerms: z.boolean().refine((val) => val === true, {
    message: "You must agree to the terms to proceed",
  }),
  isUrgent: z.boolean().optional(),
  userProvidedMissingInfo: z.string().optional(),
  userAssumptionContext: z.string().optional(),
});

export type CommercialStepData = z.infer<typeof commercialStepSchema>;

// --- Refinement Step ---

export const refinementStepSchema = z.object({
  missingInfoResponses: z.record(z.string()),
  assumptionClarifications: z.string().optional(),
  additionalLoomUrl: z.string().url("Valid Loom URL required").optional().or(z.literal("")),
});

export type RefinementStepData = z.infer<typeof refinementStepSchema>;

// --- AI Scoping Response ---

export const aiScopingResponseSchema = z.object({
  category: z.string(),
  scope_summary: z.array(z.string()).max(4),
  assumptions: z.array(z.string()),
  missing_info: z.array(z.string()),
  estimate_hours_number: z.number().min(0).max(16),
  estimate_bucket: z.enum(["small", "medium", "large"]).optional(),
  recommended_next_steps: z.string(),
  confidence_score: z.number().min(0).max(1).optional(),
  help_docs: z.array(z.object({
    title: z.string(),
    url: z.string(),
    relevance: z.string().optional(),
  })).optional(),
  knowledgebase_match: z.object({
    matched: z.boolean(),
    entry_id: z.string().optional(),
    adjusted_estimate_minutes: z.number().optional(),
    self_service_possible: z.boolean().optional(),
  }).optional(),
  requiresPayment: z.boolean().optional(),
  coverageReason: z.string().optional(),
  matchedClientId: z.string().optional(),
  scopingStatus: z.enum(["ok", "unavailable"]).optional(),
});

export type AIScopingResponse = z.infer<typeof aiScopingResponseSchema>;
